(function () {
  'use strict';

  angular.module('web2print').factory('Common', Common);

  Common.inject = ['$localStorage', '$http', '$window'];

  function Common ($localStorage, $http, apiFactory, $window) {
    const service = {
      _observers: {},

      /**
			 * Get stored value with given key.
			 * Note that ES5 does not support proxies, so this set is some sort of a workaround
			 * @param {string} path Unique key / ID pointing to value
			 * @param {*} defaultValue (Optional) A default value if no value has been set
			 */
      get: get,

      /**
			 * Store a value and bind it to given key.
			 * Note that ES5 does not support proxies, so these set is some sort of a workaround
			 * @param {string} path Unique key / ID pointing to value
			 * @param {*} value
			 */
      set: set,

      /**
			 * Check whether key contains a value.
			 * @param {string} path Unique key / ID pointing to value
			 * @returns {boolean} true if an value exists else false
			 */
      exists: exists,

      /**
			 * Remove a value.
			 * @param {string|array} paths Unique key(s) / ID(s) pointing to value
			 * @return {boolean} true if the value was deleted else false
			 */
      remove: remove,

      /**
			 * Register an observer to be run when called upon.
			 * @param {string} path Unique key / ID of Observer
			 * @param {Function} callback Function to be run when observer is invoked
			 */
      registerObserver: registerObserver,

      /**
			 * Notify and invoke observer.
			 * @param {string} path Unique key / ID of Observer
			 * @param {*} param (Optional) Parameter passed to observer
			 */
      notifyObserver: notifyObserver,

      calculateMorphTotalPrice: calculateMorphTotalPrice,
      getPriceForParcel: getPriceForParcel,
      getTotalPriceForParcels: getTotalPriceForParcels,
      getVatPriceForParcel: getVatPriceForParcel,
      getTotalVatPriceForParcels: getTotalVatPriceForParcels,
      groupSimilarParcels: groupSimilarParcels,
      groupByParcelname: groupByParcelname,
      checkObserver: checkObserver,
      checkZoneCount: checkZoneCount,
      getServiceCountry: getServiceCountry,
      resetOrderData: resetOrderData,
      getUserProfile: getUserProfile,
      checkUserTypeAndOrder: checkUserTypeAndOrder,
      getAppCountry: getAppCountry,
      getServiceNameTranslationKey: getServiceNameTranslationKey,
      formatNumber: formatNumber
    };
    return service;

    ////////////////

    /**
		 * Get stored value with given key.
		 * Note that ES5 does not support proxies, so this set is some sort of a workaround
		 * @param {string} path Unique key / ID pointing to value
		 * @param {*} defaultValue (Optional) A default value if no value has been set
		 */
    function get (path, defaultValue) {
      return _.get($localStorage, path, defaultValue);
    }

    /**
		 * Store a value and bind it to given key.
		 * Note that ES5 does not support proxies, so these set is some sort of a workaround
		 * @param {string} path Unique key / ID pointing to value
		 * @param {*} value
		 */
    function set (path, value) {
      _.set($localStorage, path, value);
    }

    /**
		 * Check whether key contains a value.
		 * @param {string} path Unique key / ID pointing to value
		 * @return {boolean} true if an value exists else false
		 */
    function exists (path) {
      const value = _.get($localStorage, path);
      return value ? true : false;
    }

    /**
		 * Remove a value.
		 * @param {string|array} paths Unique key(s) / ID(s) pointing to value
		 * @return {boolean} true if the value was deleted else false
		 */
    function remove (paths) {
      return _.unset($localStorage, typeof paths === 'string' ? [paths] : paths);
    }

    /**
		 * Register an observer to be run when called upon.
		 * @param {string} path Unique key / ID of Observer
		 * @param {Function} callback Function to be run when observer is invoked
		 */
    function registerObserver (path, callback) {
      _.set(this._observers, path, callback);
    }

    /**
		 * Notify and invoke observer.
		 * @param {string} path Unique key / ID of Observer
		 * @param {*} param (Optional) Parameter passed to observer
		 */
    function notifyObserver (path, param) {
      const callback = _.get(this._observers, path);
      if (!callback)
        throw 'Cannot find observer for path \'' + path + '\'';
      return callback(param);
    }

    function getPriceForParcel (parcel) {
      if (!parcel) return;

      const basePrice = parcel.price;
      const additionalServicesPrice = _.chain(parcel.additionalServices)
        .filter({
          selected: true
        })
        .map('price')
        .flatten()
        .reduce(function (sum, price) {
          return sum + price;
        }, 0)
        .value();
      return basePrice + additionalServicesPrice;
    }

    function getTotalPriceForParcels (parcels) {
      return _.reduce(parcels, function (sum, parcel) {
        return sum + getPriceForParcel(parcel);
      }, 0);
    }

    function getVatPriceForParcel (parcel) {
      if (!parcel) return;

      const basePrice = Math.round((1 + (parcel.vat / 10000)) * parcel.price);
      const additionalServicesPrice = _.chain(parcel.additionalServices)
        .filter({
          selected: true
        })
        .reduce(function (sum, additionalService) {
          return sum + calculateMorphTotalPrice(parcel, additionalService);
        }, 0)
        .value();
      return basePrice + additionalServicesPrice;
    }

    function getTotalVatPriceForParcels (parcels) {
      return _.reduce(parcels, function (sum, parcel) {
        return sum + getVatPriceForParcel(parcel);
      }, 0);
    }

    function groupSimilarParcels (parcels) {
      return _.chain(parcels)
        .groupBy(function (parcel) {
          const selectedAdditionalServices = _.filter(parcel.additionalServices, {
            selected: true
          });
          let name = parcel.name;
          name += (selectedAdditionalServices.length > 0) ? ' | ' + _.pluck(selectedAdditionalServices, 'name') : '';
          return name;
        })
        .map(function (parcels) {
          const selectedAdditionalServices = _.filter(parcels[0].additionalServices, {
            selected: true
          });

          return {
            name: parcels[0].name,
            parcels: parcels,
            totalPrice: getTotalPriceForParcels(parcels),
            additionalServiceNames: _.pluck(selectedAdditionalServices, 'name')
          };
        })
        .value();
    }

    function groupByParcelname (parcels) {
      return _.chain(parcels)
        .groupBy('name')
        .map(function (parcels) {

          return {
            name: parcels[0].name,
            parcels: parcels,
            dimensions: parcels[0].dimensions,
            maxWeight: parcels[0].maxWeight
          };
        })
        .value();
    }

    function calculateMorphTotalPrice (parcel, service) {
      if (service.baseProductId) {
        return Math.round((parcel.price + service.price) * (1 + (service.vat / 10000))) - parcel.price;
      } else {
        return (service.price * (1 + (service.vat / 10000)));
      }
    }

    function checkObserver (observer) {
      return this._observers[observer] || false;
    }

    function checkZoneCount () {
      let showZoneResult = false;
      const zoneCount = _.get($localStorage, 'recipients.zoneCount', {});

      if ((zoneCount[1] || zoneCount[2] || zoneCount[3])) {
        showZoneResult = true;
      }
      return showZoneResult;
    }

    function getServiceCountry () {
      return web2print.env.serviceCountry || 'SE';
    }

    function resetOrderData () {
      delete $localStorage.productDelivery;
      delete $localStorage.orderId;
      delete $localStorage.serviceType;
      delete $localStorage.pricing;
      delete $localStorage.recipients;
      delete $localStorage.materialDetails;
      delete $localStorage.email;
      delete $localStorage.s3Pdf;
      delete $localStorage.sendoutName;
      delete $localStorage.senderInfo;
      delete $localStorage.address;
      delete $localStorage.addressesFolderId;
      delete $localStorage.cartId;
      delete $localStorage.materialFolderId;
      delete $localStorage.orderPdf;
      delete $localStorage.originalAddressFileName;
      delete $localStorage.pdf;
      delete $localStorage.pdfApproved;
      delete $localStorage.pdfFileName;
      delete $localStorage.preface;
      delete $localStorage.previewOrder;
      delete $localStorage.receiptUrl;
      delete $localStorage.receiverCountryCode;
      delete $localStorage.referenceAddressInfo;
      delete $localStorage.singleSided;
      delete $localStorage.paymentStatus;
      delete $localStorage.customer;
      delete $localStorage.referenceAddressInfo;
      delete $localStorage.addReferences;
      delete $localStorage.adrData;
      delete $localStorage.currentForm;
      delete $localStorage.paymentMethod;
      delete $localStorage.pristine;
      delete $localStorage.validColumns;
      delete $localStorage.companyCache;
      delete $localStorage.zipcodes;
      delete $localStorage.selectedAreas;
      delete $localStorage.odrDistribution;
      delete $localStorage.changedAddress;
      delete $localStorage.odrData;
      delete $localStorage.uploadDateTime;
    }

    function getUserProfile (isNCP) {
      if (!get('userProfile', false) && pnTopbar.isLoggedIn) {
        apiFactory.getPublicUserProfile.get().$promise.then(function (res) {
          set('userProfile', res);
        }).catch(function (err) {
          console.log(err);
        });
      }
    }

    function checkUserTypeAndOrder (isNCP) {
      const userType = isNCP ? 'company' : 'public';
      const validUserTypes = [userType, 'any']; //any is a valid option for DK
      const product = get('productDelivery', false);

      const productUserType = product && ((product.pricingInfo || {}).deliveryOptions || {}).userType;

      if (productUserType && validUserTypes.indexOf(productUserType) <0) {
        delete $localStorage.productDelivery;
      }
    }

    function getAppCountry () {
      const isLocal = $window.location.href.indexOf('localhost') > -1;

      // this is for local development
      if (isLocal) {
        return pnTopbar.market;
      }

      return 'SE';
    }

    function getServiceNameTranslationKey (serviceType) {
      const states = {
        'admail-letter': 'ADMAIL',
        'admail-postcard': 'POSTCARD',
        'admail-odr': 'LANDING_PAGE_ADMAIL_ODR',
        'admail-flyers': 'FLYER',
        'information': 'LETTER',
        'paymentSlips': 'LANDING_PAGE_LETTER_SLIP',
        'DKLetters': 'LETTER'
      };

      return states[serviceType];
    }


    /**
     * Locale aware function for formatting numbers, for display in UI.
     *
     * Use with angular two-way binding to automatically update format with change of language.
     *
     * @param {Number} number
     * @returns {String} a formatted string
     */
    function formatNumber (number) {
      const numParsed = Number(number);
      if (isNaN(numParsed)) {
        return numParsed;
      }
      const parts = numParsed.toString().split('.'); // remove decimals (if any) from calculation
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, (function () {
        switch (pnTopbar.language) {
          case 'en': return ','; // 10,000
          case 'sv': return ' '; // 10 000
          default: return ','; // defaults to english format
        }
      }()));
      return parts.join('.');
    }
  }
})();
